/**
 * Preview Shareholder Helper
 *
 * Computes calculator results and display data for an arbitrary shareholder
 * from the cap table, enabling template previews without affecting the live UI.
 *
 * Usage:
 *   const context = computeShareholderPreview(userid, clientData, capTableData, calculationService, client);
 *   window._previewContext = context;
 *   // Then previewHeadlines() and previewEquityCards() will use this context
 */

import { buildCalculatorInput, aggregateEventData, getClientCalculatorOptions } from './calculator-input-builder.js';

/**
 * Compute all display data for a shareholder, mirroring getEquityData/renderEquityData.
 *
 * @param {string} userid - Email/userid of the shareholder to preview
 * @param {Object} clientData - Full client config from sessionStorage
 * @param {Array} capTableData - Cap table array
 * @param {Object} calculationService - CalculationService instance
 * @param {string} client - Client slug (e.g., "llproducts")
 * @param {Object} [txOverrides] - Optional per-share-class transactions to overlay onto
 *   clientData.equity (used by /admin/profile preview for EXTERNAL_DATA clients where
 *   clientData.equity[c].transactions doesn't include the previewed shareholder's rows).
 *   Shape: { [shareClass]: Transaction[] }. Does NOT mutate clientData.
 * @returns {Object|null} PreviewContext or null if shareholder not found
 */
export function computeShareholderPreview(userid, clientData, capTableData, calculationService, client, txOverrides) {
	if (!userid || !clientData || !capTableData) return null;

	// Find shareholder in cap table
	const holder = capTableData.find(function (h) {
		return h.userid === userid;
	});
	if (!holder) return null;

	return computePreviewForHolder(holder, clientData, capTableData, calculationService, client, txOverrides);
}

/**
 * Same computation as computeShareholderPreview, for a caller that already has the
 * full holder record in hand instead of a userid to look up in capTableData -- e.g.
 * proxy-voting.js's ONE_FOR_ALL loop, working from a /portal/user/alias-names response
 * for a sub-account that non-admin sessions' capTableData never contains (see
 * SessionStorageService.js).
 *
 * Pass `undefined`/`null` for capTableData when unavailable, NOT `[]` -- `[]` means
 * "confirmed zero holders" to resolveClassTotalShares (called via buildCalculatorInput),
 * which then skips its own clientData.equity[k].totalOwned/metrics.total_units
 * fallbacks and returns 0 (see class-aggregates.js's doc comment). classAggregates
 * (the separate "Compute class aggregates" block below) always defaults to `[]` for
 * its own iteration either way and simply reports zero totals when no live cap table
 * is available -- it doesn't call resolveClassTotalShares.
 *
 * @param {Object} holder - shape `{ userid, clients: { [client]: { shares, ... } } }`
 * @param {Object} clientData
 * @param {Array|undefined|null} capTableData - omit/undefined when unavailable (non-admin session)
 * @param {Object} calculationService
 * @param {string} client
 * @param {Object} [txOverrides]
 * @returns {Object|null} PreviewContext or null if the holder has no shares for this client
 */
export function computePreviewForHolder(holder, clientData, capTableData, calculationService, client, txOverrides) {
	if (!holder || !clientData) return null;

	const userid = holder.userid;
	const userShares = holder.clients?.[client]?.shares;
	if (!userShares) return null;

	// If caller provided preview-scoped transactions (from CSV fetch), overlay them onto
	// a shallow copy of clientData.equity. The original clientData is never mutated.
	const equity = txOverrides ? overlayEquityTransactions(clientData.equity, txOverrides) : clientData.equity;

	// Determine share classes (same filter logic as getEquityData)
	const equityKeys = Object.keys(clientData.equity || {}).filter(function (k) {
		return k !== 'aggregate';
	});
	let shareClasses = equityKeys.filter(function (k) {
		return userShares[k] && userShares[k].owned !== undefined;
	});

	// Apply equity_sort_order if defined (same as index.js)
	const sortOrder = clientData.equity_sort_order;
	if (sortOrder) {
		const sorted = sortOrder.filter(function (c) {
			return shareClasses.includes(c);
		});
		const remaining = shareClasses.filter(function (c) {
			return !sortOrder.includes(c);
		});
		shareClasses = sorted.concat(remaining);
	}

	// A shareholder can legitimately hold zero shares (e.g. enrolled but not yet
	// granted) and still gets a dashboard — an empty-state one, not an error. Don't
	// bail here; flow through with shareClasses: [] so callers can render that same
	// empty state instead of treating the previewed shareholder as unloadable. See #5836.

	// Aggregate events + transactions for this shareholder. Uses `equity` (with
	// preview-scoped transactions overlaid, if provided) rather than clientData.equity.
	const events = aggregateEventData(userShares, equity, shareClasses, userid);

	// Build calculator input. Pass through the overlaid equity via a shallow clone of
	// clientData so downstream consumers (valuations, strike prices) still see the rest
	// of the config intact. When no override was provided, pass the original clientData
	// by reference (no copy cost).
	// buildCalculatorInput() returns null for an empty shareClasses array, and every
	// calculator's calculate() assumes a non-null input (reads input.holdings
	// unconditionally) — so skip invoking the calculator entirely for a zero-share
	// preview rather than crash. calcResult consumers downstream (HeadlineService,
	// equity cards) all default gracefully on a null/undefined calcResult. See #5836.
	let calcResult = null;
	if (shareClasses.length > 0) {
		const effectiveClientData = txOverrides ? Object.assign({}, clientData, { equity }) : clientData;
		const calcInput = buildCalculatorInput({
			userShares,
			clientData: effectiveClientData,
			shareClasses,
			userIds: userid,
			eventData: events,
			options: getClientCalculatorOptions(client),
			capTableData,
			client,
		});

		const calculator = calculationService.getCalculatorForClient(clientData);
		calcResult = calculator.calculate(calcInput);
	}

	// Compute latestValue per class (mirrors getEquityData logic)
	const latestValue = {};
	const bases = {};
	const distribs = {};
	const contributions = {};
	const returnsOfCapital = {};
	const interest = {};

	shareClasses.forEach(function (k) {
		const owned = Number(userShares[k]?.owned) || 0;
		const sharePrice = Number(clientData.equity[k]?.valuations?.[clientData.equity[k]?.valuations?.length - 1]?.sharePrice) || 0;
		const strikePrice = Number(userShares[k]?.strikePrice) || 0;

		latestValue[k] = owned * (sharePrice - strikePrice);
		bases[k] = events.bases?.[k] ?? 0;
		distribs[k] = events.distributions?.[k] ?? 0;
		contributions[k] = events.contributions?.[k] ?? 0;
		returnsOfCapital[k] = events.returnsOfCapital?.[k] ?? 0;
		interest[k] = 0;

		// Compute interest from accrual events (for ACC-style clients)
		const classEvents = clientData.equity[k]?.events || [];
		const userClassEvents = userShares[k]?.events || [];
		userClassEvents.forEach(function (ue) {
			const ce = classEvents.find(function (e) {
				return e.nrid === ue.nrid;
			});
			if (!ce) return;
			const amt = Number(ue.grossDollars) || 0;
			if (ce.eventType === 'Interest Accrual') {
				interest[k] += amt;
			} else if (ce.eventType === 'Interest Payment') {
				interest[k] -= amt;
			}
		});
	});

	// Override latestValue from calculator breakdown if available
	if (calcResult?.breakdown) {
		calcResult.breakdown.forEach(function (cls) {
			if (cls.classKey && cls.value !== undefined) {
				latestValue[cls.classKey] = cls.value;
			}
		});
	}

	// Compute class aggregates (totalOwned per class from cap table). Unlike the
	// capTableData passed to buildCalculatorInput above, this loop needs a real array to
	// iterate -- default to [] here only, not for the whole function (resolveClassTotalShares
	// treats capTableData:[] as "confirmed zero holders" and skips its own totalOwned/
	// metrics.total_units fallbacks, so an undefined/null capTableData must stay that way
	// for callers like buildCalculatorInput -- see class-aggregates.js's doc comment).
	const classAggregates = {};
	const capTableForAggregates = Array.isArray(capTableData) ? capTableData : [];
	shareClasses.forEach(function (k) {
		let totalOwned = 0;
		let shareholderCount = 0;
		capTableForAggregates.forEach(function (h) {
			const shares = h.clients?.[client]?.shares?.[k];
			if (shares && Number(shares.owned)) {
				totalOwned += Number(shares.owned);
				shareholderCount++;
			}
		});
		classAggregates[k] = { totalOwned, shareholderCount };
	});

	return {
		userData: holder,
		userShares,
		shareKeys: shareClasses,
		bases,
		latestValue,
		distribs,
		contributions,
		returnsOfCapital,
		interest,
		calculationResult: calcResult,
		classAggregates,
	};
}

/**
 * Build a shallow-cloned equity map with per-class transactions replaced by the
 * preview-scoped overrides. Original clientData.equity is never mutated.
 *
 * Only classes present in `txOverrides` get their `transactions` replaced. Other
 * classes pass through by reference (no copy).
 *
 * @param {Object} equity - clientData.equity map
 * @param {Object} txOverrides - { [shareClass]: Transaction[] }
 * @returns {Object} New equity map
 */
function overlayEquityTransactions(equity, txOverrides) {
	if (!equity) return equity;
	const overlay = {};
	Object.keys(equity).forEach((c) => {
		if (Object.prototype.hasOwnProperty.call(txOverrides || {}, c)) {
			overlay[c] = Object.assign({}, equity[c], { transactions: txOverrides[c] });
		} else {
			overlay[c] = equity[c];
		}
	});
	return overlay;
}
